import { api } from '@goaltree/backend/convex/_generated/api'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@goaltree/ui/components/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@goaltree/ui/components/popover'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { type AttentionKey, attentionLayout, isGoalDone } from '@goaltree/ui/lib/goal-groups'
import { cn } from '@goaltree/ui/lib/utils'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAction, useMutation, useQuery } from 'convex/react'
import type { TFunction } from 'i18next'
import {
  ChevronRight,
  ImageIcon,
  Layers,
  Pencil,
  Plus,
  Search,
  Snowflake,
  Star,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AppSidebar } from '@/components/app-sidebar'
import { CoachHeaderButton } from '@/components/coach-header-button'
import GoalCard, { FocusCard, type GoalSummary } from '@/components/goal-card'
import {
  FreezeGoalDialog,
  GoalFrost,
  goalFreezeState,
  useGoalFreezeActions,
} from '@/components/goal-freeze'
import { GoalTreeFab } from '@/components/goal-tree-fab'
import { IconTip } from '@/components/icon-tip'
import { SkeletonGoalCard } from '@/components/list-skeletons'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { SiteHeader } from '@/components/site-header'
import type { GoalGroupKey } from '@/components/sort-filter-bar'
import { GoalsFilterBar } from '@/components/sort-filter-bar'
import { VisionBoard } from '@/components/vision-board'
import { useCompleteWithUndo } from '@/hooks/use-complete-with-undo'
import { useGoals } from '@/hooks/use-goals'
import { useSaveAsTemplate } from '@/hooks/use-save-as-template'
import { useHeaderSubtitle, useScrollTitle } from '@/hooks/use-scroll-title'
import { caretOffsetFromPoint } from '@/lib/caret'
import { CATEGORY_COLORS, categoryTextColor } from '@/lib/category-color'
import { copyWithToast } from '@/lib/copy'
import { removeGoal } from '@/lib/goals-optimistic'
import { optimisticRename } from '@/lib/nodes-optimistic'
import { patchPrefs } from '@/lib/prefs-optimistic'
import { useUploadCover } from '@/lib/upload-cover'
import { goalSlug } from '@/lib/utils'
import { useOnboardingStore } from '@/store/onboarding.store'
import { useViewStore } from '@/store/view.store'

export const Route = createFileRoute('/_authenticated/goals/')({
  head: () => ({
    meta: [{ title: 'Goals — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  validateSearch: (search: Record<string, unknown>): { visualise?: boolean } =>
    search.visualise ? { visualise: true } : {},
  component: RouteComponent,
})

function SavePendingTree() {
  const { t } = useTranslation()
  const autoSaveAfterAuth = useOnboardingStore((s) => s.autoSaveAfterAuth)
  const saveFromAnonymous = useMutation(api.nodes.saveFromAnonymous)

  useEffect(() => {
    if (!autoSaveAfterAuth) return
    const tree = useOnboardingStore.getState().claimAutoSave()
    if (!tree) return
    saveFromAnonymous({
      tree,
    })
      .then(() => {
        toast.success(t('goals.pendingPlanSaved'))
        useOnboardingStore.getState().reset()
      })
      .catch((err) => {
        console.error('[dashboard] SavePendingTree error:', err)
        toast.error(t('goals.pendingPlanSaveFailed'))
      })
  }, [autoSaveAfterAuth, saveFromAnonymous, t])

  return null
}

export function GoalsEmptyState() {
  const { t } = useTranslation()
  const prefs = useQuery(api.preferences.getMyPreferences)
  const onNewGoal = () => sessionStorage.setItem('newgoal_from', window.location.pathname)
  if ((prefs as { focus?: string } | undefined)?.focus === 'habits')
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-lg font-medium">{t('goals.empty')}</p>
        <p className="mt-2 text-sm text-muted-foreground">{t('goals.bigger')}</p>
        <Link
          to="/goals/new"
          search={{ goal: undefined }}
          onClick={onNewGoal}
          className="mt-3 rounded-full border border-border/60 px-4 py-2 text-sm font-semibold text-foreground/80 transition-colors hover:border-border hover:text-foreground"
        >
          {t('goals.startGoal')}
        </Link>
      </div>
    )
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <p className="text-lg font-medium">{t('goals.empty')}</p>
      <p className="mt-2 mb-6 text-sm text-muted-foreground">{t('goals.emptyFirstDesc')}</p>

      <Link to="/goals/new" search={{ goal: undefined }} onClick={onNewGoal}>
        <button
          type="submit"
          className="group flex cursor-pointer items-center justify-center gap-2 rounded-full bg-primary p-3.5 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 hover:scale-[1.01] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 disabled:scale-100"
        >
          {t('goals.createFirst')}
          <svg
            className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M3 8h10M9 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </Link>
    </div>
  )
}

export type Goal = NonNullable<ReturnType<typeof useGoals>>[number]

type GoalCover = { coverUrl: string | null; videoUrl: string | null }

function GoalCardItem({
  goal,
  starredIds,
  cover,
  summary,
  focused = false,
  variant = 'row',
}: {
  goal: Goal
  starredIds: Set<string>
  cover?: GoalCover
  summary?: GoalSummary
  focused?: boolean
  variant?: 'row' | 'focus'
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const mouseDownPos = useRef<{ x: number; y: number } | null>(null)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressOccurred = useRef(false)
  const pressPos = useRef<{ x: number; y: number } | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const preventCloseUntil = useRef(0)
  const removeNode = useMutation(api.nodes.remove).withOptimisticUpdate((store, { nodeId }) =>
    removeGoal(store, nodeId),
  )
  const toggleStar = useMutation(api.preferences.toggleStarGoal).withOptimisticUpdate(
    (store, { goalId }) => {
      const cur = store.getQuery(api.preferences.getMyPreferences, {})
      if (!cur) return
      const ids = (cur as { starredGoalIds?: string[] }).starredGoalIds ?? []
      patchPrefs(store, {
        starredGoalIds: ids.includes(goalId) ? ids.filter((id) => id !== goalId) : [...ids, goalId],
      })
    },
  )
  const renameNode = useMutation(api.nodes.rename).withOptimisticUpdate(optimisticRename)
  const generateCover = useAction((api as any).goalImages.generateCover)
  const uploadCover = useUploadCover()
  const removeCover = useMutation(api.nodes.removeCoverImage)
  const isStarred = starredIds.has(goal._id)
  const completeWithUndo = useCompleteWithUndo()
  const { frozen, until: frozenUntil } = goalFreezeState(goal.freezes)
  const [freezeOpen, setFreezeOpen] = useState(false)
  const { unfreeze } = useGoalFreezeActions(goal._id)
  const completeNext = () => {
    const taskId = summary?.nextTask?._id
    if (taskId) completeWithUndo(taskId)
  }
  const generateVisual = useAction(api.visionBoardActions.generateImage)
  const [addingVisual, setAddingVisual] = useState(false)
  const addVisual = () => {
    setAddingVisual(true)
    generateVisual({ goalId: goal._id })
      .catch(() => toast.error(t('visionboard.imageError')))
      .finally(() => setAddingVisual(false))
  }
  const coverUrl = cover?.coverUrl ?? cover?.videoUrl ?? undefined
  const saveAsTemplate = useSaveAsTemplate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<{ x: number; y: number } | null>(null)
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(goal.title)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [generatingCover, setGeneratingCover] = useState(false)
  const renamingRef = useRef<HTMLTextAreaElement>(null)
  const isTouch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
  const tapPositionRef = useRef<{ x: number; y: number } | null>(null)
  useEffect(() => {
    const el = renamingRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
    const pos = tapPositionRef.current
    if (pos) {
      tapPositionRef.current = null
      const offset = caretOffsetFromPoint(el, pos.x, pos.y)
      el.setSelectionRange(offset, offset)
    }
  }, [renaming])

  function startLongPress(clientX: number, clientY: number) {
    if (menuOpen) return
    pressPos.current = { x: clientX, y: clientY }
    longPressTimer.current = setTimeout(() => {
      const rect = cardRef.current?.getBoundingClientRect()
      if (rect && pressPos.current) {
        setMenuAnchor({ x: pressPos.current.x - rect.left, y: pressPos.current.y - rect.top })
      }
      longPressOccurred.current = true
      setMenuOpen(true)
      preventCloseUntil.current = Date.now() + 600
    }, 500)
  }
  function cancelLongPress() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  function handleMouseDown(e: React.MouseEvent) {
    mouseDownPos.current = { x: e.clientX, y: e.clientY }
  }

  function handleClick(e: React.MouseEvent) {
    if (longPressOccurred.current) {
      longPressOccurred.current = false
      return
    }
    const down = mouseDownPos.current
    mouseDownPos.current = null
    if (!down) return
    if (Math.sqrt((e.clientX - down.x) ** 2 + (e.clientY - down.y) ** 2) > 4) return
    navigate({ to: '/goals/$goalId', params: { goalId: goalSlug(goal.title, goal._id) } })
  }

  return (
    <Popover
      open={menuOpen}
      onOpenChange={(open) => {
        if (!open && Date.now() < preventCloseUntil.current) return
        setMenuOpen(open)
      }}
    >
      <div
        data-goal-id={goal._id}
        style={{
          maxHeight: isDeleting ? 0 : 400,
          overflow: 'hidden',
          transition: 'max-height 300ms ease',
        }}
      >
        <div
          ref={cardRef}
          onMouseDown={(e) => {
            handleMouseDown(e)
            startLongPress(e.clientX, e.clientY)
          }}
          onMouseUp={cancelLongPress}
          onMouseLeave={cancelLongPress}
          onTouchStart={(e) => {
            startLongPress(e.touches[0].clientX, e.touches[0].clientY)
          }}
          onTouchEnd={cancelLongPress}
          onTouchMove={cancelLongPress}
          onClick={handleClick}
          className="group/card relative cursor-pointer"
        >
          {/* Anchor positioned at long-press cursor location */}
          {menuAnchor && (
            <div
              style={{
                position: 'absolute',
                left: menuAnchor.x,
                top: menuAnchor.y,
                width: 0,
                height: 0,
                pointerEvents: 'none',
              }}
            >
              <PopoverTrigger
                style={{
                  width: 0,
                  height: 0,
                  position: 'absolute',
                  pointerEvents: 'none',
                  overflow: 'hidden',
                }}
                aria-hidden
                tabIndex={-1}
              />
            </div>
          )}
          {variant === 'focus' ? (
            <FocusCard
              title={goal.title}
              category={goal.category}
              taskCount={goal.taskCount}
              remainingCount={goal.remainingCount}
              coverUrl={cover?.coverUrl}
              videoUrl={cover?.videoUrl}
              summary={summary}
              onCompleteNext={completeNext}
              focused={focused}
            />
          ) : (
            <GoalCard
              title={goal.title}
              category={goal.category}
              progress={goal.progress}
              taskCount={goal.taskCount}
              remainingCount={goal.remainingCount}
              likeCount={goal.likeCount ?? 0}
              coverUrl={cover?.coverUrl}
              videoUrl={cover?.videoUrl}
              isStarred={isStarred}
              summary={summary}
              onCompleteNext={completeNext}
              onAddVisual={addVisual}
              addingVisual={addingVisual}
              focused={focused}
              onTitleClick={
                renaming || isTouch
                  ? undefined
                  : (e) => {
                      e.stopPropagation()
                      tapPositionRef.current = { x: e.clientX, y: e.clientY }
                      setRenameValue(goal.title)
                      setRenaming(true)
                    }
              }
              titleElement={
                renaming ? (
                  <textarea
                    ref={renamingRef}
                    autoFocus
                    rows={1}
                    value={renameValue}
                    onChange={(e) => {
                      setRenameValue(e.target.value)
                      e.target.style.height = 'auto'
                      e.target.style.height = `${e.target.scrollHeight}px`
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        const t = renameValue.trim()
                        if (t && t !== goal.title)
                          renameNode({ nodeId: goal._id, title: t }).catch(() =>
                            toast.error("Couldn't rename"),
                          )
                        setRenaming(false)
                      }
                      if (e.key === 'Escape') {
                        setRenameValue(goal.title)
                        setRenaming(false)
                      }
                    }}
                    onBlur={() => {
                      const t = renameValue.trim()
                      if (t && t !== goal.title)
                        renameNode({ nodeId: goal._id, title: t }).catch(() =>
                          toast.error("Couldn't rename"),
                        )
                      setRenaming(false)
                    }}
                    className="w-full resize-none overflow-hidden bg-transparent text-sm font-bold leading-snug text-foreground outline-none"
                  />
                ) : undefined
              }
            />
          )}
          <GoalFrost goalId={goal._id} frozen={frozen} until={frozenUntil} rounded="rounded-xl" />

          <PopoverContent
            className="w-auto min-w-[160px] !p-1 !gap-0 [&_button]:focus-visible:outline-none"
            align="end"
            side="bottom"
          >
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
              onClick={(e) => {
                e.stopPropagation()
                setMenuOpen(false)
                toggleStar({ goalId: goal._id })
              }}
            >
              <Star className={cn('h-3.5 w-3.5', isStarred && 'fill-yellow-400 text-yellow-400')} />
              {isStarred ? t('sidebar.unstar') : t('sidebar.star')}
            </button>
            <button
              type="button"
              disabled={generatingCover}
              className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
              onClick={async (e) => {
                e.stopPropagation()
                setMenuOpen(false)
                setGeneratingCover(true)
                try {
                  await generateCover({ goalId: goal._id })
                } catch {
                  toast.error(t('common.generateCover') + ' failed')
                } finally {
                  setGeneratingCover(false)
                }
              }}
            >
              <ImageIcon className={`h-3.5 w-3.5 ${generatingCover ? 'animate-pulse' : ''}`} />
              {generatingCover
                ? t('common.generatingCover')
                : coverUrl
                  ? t('common.regenerateCover')
                  : t('common.generateCover')}
            </button>
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
              onClick={(e) => {
                e.stopPropagation()
                setMenuOpen(false)
                void uploadCover(goal._id)
              }}
            >
              <Upload className="h-3.5 w-3.5" />
              {t('common.uploadCover')}
            </button>
            {coverUrl && (
              <button
                type="button"
                className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  removeCover({ goalId: goal._id }).catch(() =>
                    toast.error(t('common.coverUploadFailed')),
                  )
                }}
              >
                <X className="h-3.5 w-3.5" />
                {t('common.removeCover')}
              </button>
            )}
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
              onClick={(e) => {
                e.stopPropagation()
                setMenuOpen(false)
                setRenameValue(goal.title)
                setRenaming(true)
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
              {t('common.rename')}
            </button>
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
              onClick={(e) => {
                e.stopPropagation()
                setMenuOpen(false)
                void saveAsTemplate(goal._id)
              }}
            >
              <Layers className="h-3.5 w-3.5" />
              {t('templates.saveAsTemplate')}
            </button>
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
              onClick={(e) => {
                e.stopPropagation()
                setMenuOpen(false)
                if (frozen) void unfreeze()
                else setFreezeOpen(true)
              }}
            >
              <Snowflake className="h-3.5 w-3.5 text-sky-500" />
              {t(frozen ? 'goalFreeze.unfreeze' : 'goalFreeze.freeze')}
            </button>
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
              onClick={(e) => {
                e.stopPropagation()
                setMenuOpen(false)
                mouseDownPos.current = null
                setConfirmDelete(true)
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('common.delete')}
            </button>
          </PopoverContent>
        </div>
        {variant === 'row' && (
          <StaleGoalNudge goal={goal} frozen={frozen} onFreeze={() => setFreezeOpen(true)} />
        )}
      </div>
      <FreezeGoalDialog goalId={goal._id} open={freezeOpen} onOpenChange={setFreezeOpen} />
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.deleteGoalTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('common.deleteGoalDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmDelete(false)
                setIsDeleting(true)
                setTimeout(() => {
                  removeNode({ nodeId: goal._id }).catch(() => {
                    setIsDeleting(false)
                    toast.error('Failed to delete')
                  })
                }, 320)
              }}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Popover>
  )
}

const STALE_DAYS = 14
const NUDGE_SNOOZE_MS = 30 * 86_400_000
const nudgeKey = (id: string) => `goaltree-stale-nudge:${id}`

// "No progress in N days — freeze it?" under goals left untouched for a while.
// "Not now" hides it for that goal for a month.
function StaleGoalNudge({
  goal,
  frozen,
  onFreeze,
}: {
  goal: Goal
  frozen: boolean
  onFreeze: () => void
}) {
  const { t } = useTranslation()
  const [snoozed, setSnoozed] = useState(() => {
    try {
      return Date.now() - Number(localStorage.getItem(nudgeKey(goal._id)) ?? 0) < NUDGE_SNOOZE_MS
    } catch {
      return false
    }
  })
  const days = Math.floor((Date.now() - (goal.lastActivityAt ?? goal._creationTime)) / 86_400_000)
  const show =
    !snoozed &&
    !frozen &&
    !goal.isInbox &&
    goal.status !== 'done' &&
    goal.progress < 100 &&
    days >= STALE_DAYS
  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="overflow-hidden"
        >
          <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-sky-300/40 bg-sky-400/5 px-2.5 py-1.5 text-xs dark:border-sky-300/20">
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {t('goalFreeze.staleNudge', { count: days })}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onFreeze()
              }}
              className="flex shrink-0 cursor-pointer items-center gap-1 font-bold text-sky-600 hover:underline dark:text-sky-300"
            >
              <Snowflake className="size-3" strokeWidth={2.4} />
              {t('goalFreeze.freezeShort')}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                try {
                  localStorage.setItem(nudgeKey(goal._id), String(Date.now()))
                } catch {}
                setSnoozed(true)
              }}
              className="shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
            >
              {t('goalFreeze.notNow')}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// The list's visual order, so j/k walks Focus then each open section.
function listOrder(
  goals: Goal[],
  starredIds: Set<string>,
  summaryMap: Record<string, GoalSummary>,
  collapsed: string[],
): Goal[] {
  const { focus, groups } = attentionLayout(
    goals,
    starredIds,
    (id) => summaryMap[id]?.pace,
    (g) => goalFreezeState(g.freezes).frozen,
  )
  return [...focus, ...groups.flatMap((g) => (collapsed.includes(g.key) ? [] : g.goals))]
}

const SECTION_LABEL: Record<AttentionKey, string> = {
  attention: 'goalsBoard.needsAttention',
  onTrack: 'goalsBoard.onTrack',
  done: 'goalsBoard.done',
}

function GoalsDashboard({
  displayGoals,
  group,
  attention,
  starredIds,
  coverMap,
  summaryMap,
  focusedGoalId,
}: {
  displayGoals: Goal[]
  group: GoalGroupKey
  // Default layout (no explicit sort or group): Focus + attention sections.
  attention: boolean
  starredIds: Set<string>
  coverMap: Record<string, GoalCover>
  summaryMap: Record<string, GoalSummary>
  focusedGoalId?: string | null
}) {
  const { t } = useTranslation()
  const goals = useGoals()
  const collapsed = useViewStore((s) => s.goalsCollapsed)
  const toggleSection = useViewStore((s) => s.toggleGoalsSection)

  if (goals === undefined) {
    return (
      <section>
        <div className="mb-2 flex h-4 items-center">
          <div className="h-2.5 w-16 animate-pulse rounded bg-muted/60" />
        </div>
        <div className="flex flex-col gap-2">
          {[58, 70, 46].map((w) => (
            <SkeletonGoalCard key={w} title={w} />
          ))}
        </div>
      </section>
    )
  }

  // No goals at all (the inbox pseudo-goal doesn't count) — show the first-goal
  // empty state. The parent hides the filter bar in this case.
  if (goals.filter((g) => !g.isInbox).length === 0) {
    return <GoalsEmptyState />
  }

  // Goals exist but the active filters exclude them all — keep the filter/sort/
  // group bar (rendered by the parent) so the user can adjust.
  if (displayGoals.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No goals match the current filters.
      </p>
    )
  }

  const row = (goal: Goal) => (
    <motion.div
      key={goal._id}
      layout
      layoutDependency={group}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <GoalCardItem
        goal={goal}
        starredIds={starredIds}
        cover={coverMap[goal._id]}
        summary={summaryMap[goal._id]}
        focused={focusedGoalId === goal._id}
      />
    </motion.div>
  )

  if (attention) {
    const { focus, groups } = attentionLayout(
      displayGoals,
      starredIds,
      (id) => summaryMap[id]?.pace,
      (g) => goalFreezeState(g.freezes).frozen,
    )
    return (
      <div className="flex flex-col gap-5">
        {focus.length > 0 && (
          <section>
            <SectionLabel label={t('goalsBoard.focus')} hint={t('goalsBoard.starred')} />
            {/* Phones: one swipeable row of cover cards (a peek of the next says
                "scroll"), so the rest of the list stays above the fold. From sm the
                page column fits a 2–3 column grid. */}
            <div
              className={cn(
                '-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-4 px-4 pb-1 sm:mx-0 sm:grid sm:snap-none sm:grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] sm:overflow-visible sm:px-0 sm:pb-0',
                '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
              )}
            >
              {focus.map((goal) => (
                <div
                  key={goal._id}
                  className={cn(
                    'shrink-0 snap-start sm:w-auto',
                    focus.length > 1 ? 'w-[82%]' : 'w-full',
                  )}
                >
                  <GoalCardItem
                    goal={goal}
                    starredIds={starredIds}
                    cover={coverMap[goal._id]}
                    summary={summaryMap[goal._id]}
                    focused={focusedGoalId === goal._id}
                    variant="focus"
                  />
                </div>
              ))}
            </div>
          </section>
        )}
        {groups
          .filter(({ goals: gs }) => gs.length > 0)
          .map(({ key, goals: gs }) => {
            const isCollapsed = collapsed.includes(key)
            const empty = gs.length === 0
            return (
              <section key={key}>
                <button
                  type="button"
                  onClick={() => toggleSection(key)}
                  disabled={empty}
                  aria-expanded={!isCollapsed && !empty}
                  className="group/sec mb-2 flex w-full cursor-pointer items-center gap-2 text-left disabled:cursor-default"
                >
                  <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    {t(SECTION_LABEL[key])}
                  </span>
                  <span className="text-xs text-muted-foreground/70 tabular-nums">{gs.length}</span>
                  {!empty && (
                    <ChevronRight
                      className={cn(
                        'h-3.5 w-3.5 text-muted-foreground/60 transition-transform group-hover/sec:text-foreground',
                        !isCollapsed && 'rotate-90',
                      )}
                    />
                  )}
                </button>
                {!isCollapsed && !empty && <div className="flex flex-col gap-2">{gs.map(row)}</div>}
              </section>
            )
          })}
      </div>
    )
  }

  // An explicit group shows those groups; an explicit sort alone is a flat list.
  const grouped = groupGoals(displayGoals, group, t)
  return (
    <div className="flex flex-col gap-2">
      {grouped.map((g, gi) => (
        <div key={`${g.label}-${gi}`} className="flex flex-col gap-2">
          {group !== 'none' && (
            <div className={cn('-mb-1', gi > 0 && 'mt-3')}>
              <GoalGroupHeader label={g.label} color={g.color} count={g.goals.length} />
            </div>
          )}
          {g.goals.map(row)}
        </div>
      ))}
    </div>
  )
}

function SectionLabel({ label, hint }: { label: string; hint?: string }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      {hint && <span className="text-xs text-muted-foreground/70">{hint}</span>}
    </div>
  )
}

const STATUS_ORDER: Record<string, number> = { backlog: 0, 'in-progress': 1, done: 2 }
const STATUS_LABELS: Record<string, string> = {
  backlog: 'goals.backlog',
  'in-progress': 'goals.inProgress',
  done: 'goals.done',
}

function effectiveStatus(g: Goal): 'backlog' | 'in-progress' | 'done' {
  if (g.status === 'done') return 'done'
  if (g.status === 'in-progress' || g.progress > 0 || g.inProgressTaskCount > 0)
    return 'in-progress'
  return 'backlog'
}

function groupGoals(
  goals: Goal[],
  group: GoalGroupKey,
  t: TFunction,
): { label: string; color?: string; goals: Goal[] }[] {
  if (group === 'none') return [{ label: '', goals }]

  const map = new Map<string, Goal[]>()
  for (const goal of goals) {
    const key = group === 'category' ? goal.category.toLowerCase() : effectiveStatus(goal)
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(goal)
  }

  const entries = Array.from(map.entries())
  if (group === 'status') {
    entries.sort(([a], [b]) => (STATUS_ORDER[a] ?? 99) - (STATUS_ORDER[b] ?? 99))
  }

  return entries.map(([key, goals]) => ({
    label: group === 'category' ? key : STATUS_LABELS[key] ? t(STATUS_LABELS[key]) : key,
    color: group === 'category' ? CATEGORY_COLORS[key] : undefined,
    goals,
  }))
}

function GoalGroupHeader({
  label,
  color,
  count,
}: {
  label: string
  color?: string
  count: number
}) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <span
        className="text-xs font-bold uppercase tracking-widest"
        style={color ? { color: categoryTextColor(color) } : undefined}
      >
        {label}
      </span>
      <span className="text-xs text-muted-foreground tabular-nums">{count}</span>
    </div>
  )
}

function applyGoalsFilter(
  goals: Goal[],
  filter: { categories: string[]; sort: string; type: string; hideCompleted?: boolean },
  starredIds: Set<string>,
): Goal[] {
  let result = goals
  if (filter.hideCompleted) {
    result = result.filter((g) => g.status !== 'done')
  }
  if (filter.type === 'starred') {
    result = result.filter((g) => starredIds.has(g._id))
  } else if (filter.type === 'recent') {
    result = result.filter((g) => !starredIds.has(g._id))
  }
  if (filter.categories.length > 0) {
    result = result.filter((g) => filter.categories.includes(g.category.toLowerCase()))
  }
  switch (filter.sort) {
    case 'progress':
      result = [...result].sort((a, b) => b.progress - a.progress)
      break
    case 'remaining':
      result = [...result].sort((a, b) => b.remainingCount - a.remainingCount)
      break
    case 'title':
      result = [...result].sort((a, b) => a.title.localeCompare(b.title))
      break
    default:
      // No explicit sort: starred goals first, otherwise keep the natural order.
      result = [...result].sort(
        (a, b) => Number(starredIds.has(b._id)) - Number(starredIds.has(a._id)),
      )
  }
  return result
}

function RouteComponent() {
  const { t } = useTranslation()
  const { onScroll } = useScrollTitle(t('goals.title'))
  const navigate = useNavigate()
  const { visualise } = Route.useSearch()
  const goals = useGoals()
  const coverUrlsData = useQuery(api.nodes.getGoalCoverUrls) ?? []
  const coverMap: Record<string, GoalCover> = Object.fromEntries(
    coverUrlsData.map(({ goalId, ...c }) => [goalId, c]),
  )
  const summaries = useQuery(api.projection.getGoalSummaries)
  const summaryMap: Record<string, GoalSummary> = Object.fromEntries(
    (summaries ?? []).map(({ goalId, ...s }) => [goalId, s]),
  )
  const paceCounts = (summaries ?? []).reduce(
    (acc, s) => {
      if (s.pace) acc[s.pace]++
      return acc
    },
    { onPace: 0, behind: 0, quiet: 0 },
  )
  const [fabExpanded, setFabExpanded] = useState(true)
  const visionActive = useViewStore((s) => s.goalsVision)
  const setVisionActive = useViewStore((s) => s.setGoalsVision)
  const [focusedGoalId, setFocusedGoalId] = useState<string | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const lastScrollY = useRef(0)
  const fabExpandedRef = useRef(true)
  const scrollRef = useRef<HTMLDivElement>(null)
  const scrollRestoredRef = useRef(false)

  // Persist the list scroll and restore it on return. Android's hardware/gesture
  // back can reload this route (fresh document); the goals then load async, so
  // TanStack's element scroll restoration runs against an empty list and clamps
  // to 0. Save on leave (pagehide + unmount) and restore once the list is tall
  // enough — survives both a full reload and an SPA remount.
  useEffect(() => {
    const save = () => {
      if (scrollRef.current) {
        sessionStorage.setItem('goals-scroll', String(scrollRef.current.scrollTop))
      }
    }
    window.addEventListener('pagehide', save)
    return () => {
      window.removeEventListener('pagehide', save)
      save()
    }
  }, [])
  const kbStateRef = useRef<{
    displayGoals: Goal[]
    focusedGoalId: string | null
    helpOpen: boolean
    setHelpOpen: (v: boolean) => void
  } | null>(null)
  const filter = useViewStore((s) => s.goalsFilter)
  const setFilter = useViewStore((s) => s.setGoalsFilter)
  const resetFilter = useViewStore((s) => s.resetGoalsFilter)

  const prefs = useQuery(api.preferences.getMyPreferences)
  const starredIds = new Set<string>(
    (prefs as { starredGoalIds?: string[] } | null | undefined)?.starredGoalIds ?? [],
  )

  const regularGoals = goals?.filter((g) => !g.isInbox)
  const frozenCount = regularGoals?.filter((g) => goalFreezeState(g.freezes).frozen).length ?? 0
  const achievedCount = regularGoals?.filter(isGoalDone).length ?? 0
  const displayGoals = regularGoals ? applyGoalsFilter(regularGoals, filter, starredIds) : []
  const attention = filter.sort === 'manual' && filter.group === 'none'
  const collapsed = useViewStore((s) => s.goalsCollapsed)

  kbStateRef.current = {
    displayGoals: attention
      ? listOrder(displayGoals, starredIds, summaryMap, collapsed)
      : displayGoals,
    focusedGoalId,
    helpOpen,
    setHelpOpen,
  }

  // Deep link from the morning reminder: open the board and start Visualise.
  useEffect(() => {
    if (visualise) setVisionActive(true)
  }, [visualise, setVisionActive])
  const clearVisualise = useCallback(
    () => navigate({ to: '/goals', search: {}, replace: true }),
    [navigate],
  )

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
        return
      const kb = kbStateRef.current
      if (!kb) return

      if (e.key === '?' || e.key === 'Escape') {
        if (kb.helpOpen) {
          e.preventDefault()
          kb.setHelpOpen(false)
        } else if (e.key === '?') {
          e.preventDefault()
          kb.setHelpOpen(true)
        }
        return
      }
      if (kb.helpOpen) return

      const nav = kb.displayGoals
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault()
        if (!nav.length) return
        const idx = nav.findIndex((g) => g._id === kb.focusedGoalId)
        const next = nav[idx === -1 ? 0 : Math.min(idx + 1, nav.length - 1)]
        setFocusedGoalId(next._id)
        document
          .querySelector(`[data-goal-id="${next._id}"]`)
          ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault()
        if (!nav.length) return
        const idx = nav.findIndex((g) => g._id === kb.focusedGoalId)
        const prev = nav[idx <= 0 ? 0 : idx - 1]
        setFocusedGoalId(prev._id)
        document
          .querySelector(`[data-goal-id="${prev._id}"]`)
          ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      } else if (e.key === 'Enter') {
        if (!kb.focusedGoalId) return
        e.preventDefault()
        const goal = nav.find((g) => g._id === kb.focusedGoalId)
        if (goal)
          navigate({ to: '/goals/$goalId', params: { goalId: goalSlug(goal.title, goal._id) } })
      } else if (e.key === 'c') {
        if (!kb.focusedGoalId) return
        e.preventDefault()
        const goal = nav.find((g) => g._id === kb.focusedGoalId)
        if (goal) copyWithToast(goal.title)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const viewSwitch = (
    <div
      role="tablist"
      aria-label={t('goals.title')}
      className="flex shrink-0 rounded-full border border-border p-0.5"
    >
      {([false, true] as const).map((board) => (
        <button
          key={String(board)}
          type="button"
          role="tab"
          aria-selected={visionActive === board}
          onClick={() => setVisionActive(board)}
          className={cn(
            'cursor-pointer rounded-full px-3.5 py-1 text-xs font-semibold transition-colors',
            visionActive === board
              ? 'bg-foreground text-background'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {t(board ? 'goalsBoard.board' : 'goalsBoard.list')}
        </button>
      ))}
    </div>
  )

  const mobileHeaderActions = (
    <div className="flex items-center gap-1">
      <CoachHeaderButton />
      <IconTip label={t('nav.search')}>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('open-search'))}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={t('nav.search')}
        >
          <Search className="h-5 w-5" strokeWidth={1.5} />
        </button>
      </IconTip>
    </div>
  )

  const goalCount =
    regularGoals?.filter((g) => g.remainingCount > 0 || g.taskCount === 0).length ?? 0
  useHeaderSubtitle(regularGoals?.length ? t('headerSub.goals', { count: goalCount }) : undefined)
  const headerTitle =
    regularGoals === undefined
      ? t('goals.title')
      : regularGoals.length === 0
        ? t('goals.headerNone')
        : t('goals.headerCount', { count: goalCount })

  const noGoals = regularGoals !== undefined && regularGoals.length === 0

  const goalsHeader = (
    <>
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t('goals.title')}
          </p>
          <h1 className="text-3xl font-black text-foreground">
            {visionActive ? t('visionboard.title') : headerTitle}
          </h1>
        </div>
        {!noGoals && viewSwitch}
      </div>
      {!visionActive &&
        paceCounts.onPace + paceCounts.behind + paceCounts.quiet + frozenCount + achievedCount >
          0 && (
          <p className="mt-1 font-mono text-xs text-muted-foreground">
            {[
              paceCounts.onPace > 0 && (
                <span key="on">{t('goals.summaryOnPace', { count: paceCounts.onPace })}</span>
              ),
              paceCounts.behind > 0 && (
                <span key="behind" className="text-amber-700 dark:text-amber-400">
                  {t('goals.summaryBehind', { count: paceCounts.behind })}
                </span>
              ),
              paceCounts.quiet > 0 && (
                <span key="quiet">{t('goals.summaryQuiet', { count: paceCounts.quiet })}</span>
              ),
              frozenCount > 0 && (
                <span key="frozen" className="text-sky-600 dark:text-sky-300">
                  {t('goals.summaryFrozen', { count: frozenCount })}
                </span>
              ),
              achievedCount > 0 && (
                <span key="achieved">{t('goals.summaryAchieved', { count: achievedCount })}</span>
              ),
            ]
              .filter(Boolean)
              .flatMap((el, i) => (i ? [<span key={`sep${i}`}> · </span>, el] : [el]))}
          </p>
        )}
    </>
  )

  const filterBar = regularGoals && regularGoals.length > 0 && (
    <GoalsFilterBar
      categories={filter.categories}
      sort={filter.sort}
      group={filter.group}
      type={filter.type ?? 'all'}
      hideCompleted={filter.hideCompleted}
      onCategories={(c) => setFilter({ categories: c })}
      onSort={(s) => setFilter({ sort: s })}
      onGroup={(g) => setFilter({ group: g })}
      onType={(v) => setFilter({ type: v })}
      onHideCompleted={(v) => setFilter({ hideCompleted: v })}
      onReset={resetFilter}
      showGroup
      rightSlot={
        <Link
          to="/goals/new"
          search={{ goal: undefined }}
          onClick={() => sessionStorage.setItem('newgoal_from', window.location.pathname)}
          className="hidden h-7 items-center gap-1 rounded-full bg-primary px-3 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90 lg:flex"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.6} />
          {t('goals.new')}
        </Link>
      }
    />
  )

  // Restore saved scroll once the list has rendered enough content to hold it.
  useLayoutEffect(() => {
    if (scrollRestoredRef.current || visionActive) return
    const el = scrollRef.current
    if (!el) return
    const saved = Number(sessionStorage.getItem('goals-scroll') ?? '0')
    if (saved > 0 && el.scrollHeight - el.clientHeight >= saved) {
      el.scrollTop = saved
      scrollRestoredRef.current = true
    }
  }, [goals, visionActive])

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    onScroll(e)
    const y = e.currentTarget.scrollTop
    const dy = y - lastScrollY.current
    lastScrollY.current = y
    const isAtTop = y <= 24
    if (dy > 4 && fabExpandedRef.current && !isAtTop) {
      fabExpandedRef.current = false
      setFabExpanded(false)
    } else if (dy < -4 && !fabExpandedRef.current) {
      fabExpandedRef.current = true
      setFabExpanded(true)
    }
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden bg-background relative">
        <MobileScreenHeader
          title={t('goals.title')}
          showInbox={false}
          showSearch={false}
          rightSlot={mobileHeaderActions}
          col={visionActive ? '72rem' : undefined}
        />
        <SiteHeader className="hidden lg:flex" />
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto pb-32 [scrollbar-width:none] lg:pb-0 [&::-webkit-scrollbar]:hidden"
          onScroll={handleScroll}
          data-scroll-reset
          data-scroll-restoration-id="tab-scroll"
        >
          <SavePendingTree />
          {visionActive ? (
            // Wider than the list column: a 4-column masonry needs ~1100px to keep
            // tiles legible, while the list stays at the reading width.
            <div className="px-4 pt-4 pb-4">
              <div className="mx-auto max-w-6xl">
                <div className="mb-4">{goalsHeader}</div>
                {noGoals ? (
                  <div className="flex min-h-[50vh] items-center justify-center pb-16">
                    <GoalsEmptyState />
                  </div>
                ) : (
                  <VisionBoard autoVisualise={!!visualise} onAutoVisualised={clearVisualise} />
                )}
              </div>
            </div>
          ) : noGoals ? (
            <div className="px-4 pt-4 pb-4">
              <div className="mx-auto max-w-(--page-col)">
                <div className="mb-4">{goalsHeader}</div>
                <div className="flex min-h-[50vh] items-center justify-center pb-16">
                  <GoalsEmptyState />
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 pt-4 pb-4">
              <div className="mx-auto max-w-(--page-col)">
                <div className="mb-4">
                  <div className="mb-4">{goalsHeader}</div>
                  {regularGoals === undefined ? (
                    <div className="flex gap-1.5">
                      {[56, 48, 64, 72].map((w, i) => (
                        <div
                          key={i}
                          className="h-7 animate-pulse rounded-md bg-muted"
                          style={{ width: w }}
                        />
                      ))}
                    </div>
                  ) : (
                    filterBar
                  )}
                </div>
                <GoalsDashboard
                  displayGoals={displayGoals}
                  group={filter.group}
                  attention={attention}
                  starredIds={starredIds}
                  coverMap={coverMap}
                  summaryMap={summaryMap}
                  focusedGoalId={focusedGoalId}
                />
              </div>
            </div>
          )}
        </div>
        <GoalTreeFab expanded={fabExpanded} screen="goals" />
      </SidebarInset>
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold uppercase tracking-widest text-muted-foreground/60">
              Keyboard shortcuts
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1 font-mono text-sm">
            {(
              [
                ['J / ↓', 'Next goal'],
                ['K / ↑', 'Previous goal'],
                ['Enter', 'Open goal'],
                ['C', 'Copy goal title'],
                ['B / ⌘B', 'Brain dump'],
                ['T', 'New task'],
                ['H', 'New habit'],
                ['G', 'New goal'],
                ['?', 'Show shortcuts'],
                ['Esc', 'Close'],
              ] as const
            ).map(([key, label]) => (
              <div
                key={key}
                className="flex items-center justify-between gap-4 border-b border-border/30 py-1.5 last:border-0"
              >
                <span className="text-xs text-muted-foreground">{label}</span>
                <kbd className="rounded border border-border/60 bg-muted/50 px-2 py-0.5 font-mono text-xs text-foreground/70">
                  {key}
                </kbd>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  )
}
